import { css } from "@linaria/core";
import cx from "classnames";
import React, { createContext, memo, useCallback } from "react";
import t from "../../../../../i18n";
import useToasts from "@ui/Toast/useToasts";
import ButtonIcon from "@ui/ButtonIconV2";
import {
  BracesLine,
  DeleteBinLine,
  DownloadLine,
  FileCopyLine,
  FilePdf2Line,
  GroupLine,
  LockLine,
  // LockLine,
  // LockUnlockLine,
} from "@ui/IconV2/react";
import More2Fill from "@ui/IconV2/react/More2Fill";
import Menu from "@ui/Menu";
import Option, { OptionGroup } from "@ui/Option";
import { spacings } from "@ui/Theme";
import { useExportDashboardCallback } from "../../hook/action/export";
import type { DashboardModelT } from "../../schema/dashboard";
import Text from "@ui/Text";
import { printableHidden } from "../../utils/print";
import { useModals } from "@ui/lib/hooks/useModal";
import { useUserCanEdit } from "../../hook/useUserCanEdit";
import { useDashboardLocks } from "../../hook/action/lock";
import { ModalDashboardDuplicate } from "../TransactionalModals/ModalDashboardDuplicate";
import { ModalDashboardDelete } from "../TransactionalModals/ModalDashboardDelete";
import { ModalManageSharing } from "../TransactionalModals/ModalManageSharing";
import { useCanManageSharing } from "../../hook/useCanManageSharing";
import { useHasUserRole } from "../../../../canUserDo";
import { useUser } from "../../../../../api/useUser";
import { UnlockDashboardModal } from "../../view/Dashboards/DashboardsTableListing/UnlockDashboardModal";
import { useDashboardPermissions } from "../../hook/useDashboardPermissions";
import ExportPDFOption from "../../../ExportPDFOption";

const itemContainer = css`
  min-width: 200px;
`;

const item = css`
  border-radius: 4px;
  padding: ${spacings.xs};
`;

const toggle = css`
  box-sizing: border-box;
  margin-top: 2px;
`;

const DashboardMenuContext = createContext<{
  closeExportSubMenu: (e: React.MouseEvent) => void;
}>({
  closeExportSubMenu: () => {},
});

const ExportJSONOption = memo(({ dashboard }: { dashboard: DashboardModelT }) => {
  const exportDashboardCallback = useExportDashboardCallback(dashboard);
  const { addToast } = useToasts();

  const exportCallback = useCallback(async () => {
    try {
      const { filename, hasFailedAssets } = await exportDashboardCallback();

      if (hasFailedAssets) {
        addToast("warning", t("project.dashboard.actions.export.partial_success", filename));
        return;
      }

      addToast("success", t("project.dashboard.actions.export.success", filename));
    } catch (e) {
      addToast(
        "error",
        e instanceof Error ? e.message : t("project.dashboard.actions.export.error"),
      );
    }
  }, [addToast, exportDashboardCallback]);

  return (
    <Option className={item} closeOnClick onClick={exportCallback}>
      <BracesLine />
      {t("project.dashboard.actions.export.json")}
    </Option>
  );
});

/**
 * @todo Need to refine usage and necessity for those options.
 */
// const ImportOption = memo(({ dashboard }: { dashboard: DashboardModelT }) => (
//   <Option closeOnClick onClick={() => true}>
//     <UploadLine />
//     {t("project.dashboard.actions.import")}
//   </Option>
// ));

export const DashboardMenu = ({
  dashboard,
  shouldRedirectOnDuplicate = true,
  withPrintOption = false,
}: {
  dashboard: DashboardModelT;
  shouldRedirectOnDuplicate?: boolean;
  withPrintOption?: boolean;
}) => {
  const user = useUser();
  const isSuperAdmin = useHasUserRole(user, "SUPER_ADMIN");
  const userCanEdit = useUserCanEdit(dashboard.projectId);
  const [exportOpened, setExportOpened] = React.useState(false);
  const { isLocked, isUserLocked } = useDashboardLocks(dashboard.projectId);
  const closeExportSubMenu = useCallback(() => setExportOpened(false), []);
  const { modal, openModal, closeModal } = useModals<
    "duplicate" | "sharing_management" | "delete" | "unlock"
  >();
  const canManageSharing = useCanManageSharing(dashboard);
  const dashboardPermissions = useDashboardPermissions(dashboard);

  const isAuthor = dashboard.createdByUserId === user.id;
  const canSuperAdminActOnDashboard = !isSuperAdmin || dashboard.published || isAuthor;

  return (
    <DashboardMenuContext.Provider
      value={{
        closeExportSubMenu,
      }}
    >
      <>
        <Menu
          className={printableHidden}
          placement="bottom-end"
          overlay={
            <div className={itemContainer}>
              <OptionGroup>
                {userCanEdit && canSuperAdminActOnDashboard && (
                  <Option
                    className={item}
                    closeOnClick
                    onClick={() => openModal("duplicate")}
                    onMouseEnter={closeExportSubMenu}
                    disabled={!dashboardPermissions.allowDuplication}
                  >
                    <FileCopyLine />
                    {t("project.dashboard.actions.duplicate")}
                  </Option>
                )}
                {userCanEdit && canSuperAdminActOnDashboard && (
                  <Option
                    className={item}
                    closeOnClick
                    onClick={() => openModal("sharing_management")}
                    onMouseEnter={closeExportSubMenu}
                    disabled={!dashboardPermissions.allowReadModeChange}
                    title={
                      dashboardPermissions.allowReadModeChange
                        ? ""
                        : t("project.dashboard.actions.manage_sharing.disabled_hint")
                    }
                  >
                    <GroupLine />
                    {t("project.dashboard.actions.manage_sharing")}
                  </Option>
                )}
                <Menu
                  className={printableHidden}
                  opened={exportOpened}
                  setOpened={setExportOpened}
                  placement="left-start"
                  arrowWidth={0}
                  overlay={
                    <div className={itemContainer}>
                      <ExportJSONOption dashboard={dashboard} />
                      {withPrintOption && (
                        <ExportPDFOption className={item}>
                          <FilePdf2Line />
                          {t("project.dashboard.actions.export.pdf")}
                        </ExportPDFOption>
                      )}
                    </div>
                  }
                >
                  <div>
                    <Option
                      className={cx(item, {
                        // [exportToMenuItemActive]: exportOpened,
                      })}
                      onMouseEnter={() => setExportOpened(true)}
                    >
                      <DownloadLine />
                      <Text type="body2">{t("project.dashboard.actions.export")}</Text>
                    </Option>
                  </div>
                </Menu>
              </OptionGroup>
              {userCanEdit && canSuperAdminActOnDashboard && (
                <OptionGroup>
                  <Option
                    className={cx(item)}
                    closeOnClick
                    onClick={() => openModal("delete")}
                    onMouseEnter={closeExportSubMenu}
                    disabled={!dashboardPermissions.allowDeletion}
                    title={
                      dashboardPermissions.allowDeletion
                        ? ""
                        : t("project.dashboard.actions.manage_sharing.disabled_hint")
                    }
                  >
                    <DeleteBinLine />
                    {t("project.dashboard.actions.delete")}
                  </Option>
                </OptionGroup>
              )}

              {isSuperAdmin && isLocked(dashboard.id) && !isUserLocked(dashboard.id) && (
                <OptionGroup>
                  <Option
                    closeOnClick
                    onMouseEnter={closeExportSubMenu}
                    onClick={() => openModal("unlock")}
                  >
                    <LockLine />
                    {t("project.dashboard.actions.override")}
                  </Option>
                </OptionGroup>
              )}
            </div>
          }
        >
          <ButtonIcon className={toggle}>
            <More2Fill size="l" />
          </ButtonIcon>
        </Menu>
        {userCanEdit && (
          <>
            <ModalDashboardDuplicate
              dashboard={dashboard}
              opened={modal === "duplicate"}
              close={closeModal}
              shouldRedirect={shouldRedirectOnDuplicate}
            />
            {canManageSharing && (
              <ModalManageSharing
                dashboard={dashboard}
                opened={modal === "sharing_management"}
                close={closeModal}
              />
            )}
            <ModalDashboardDelete
              dashboard={dashboard}
              opened={modal === "delete"}
              close={closeModal}
            />
          </>
        )}
        {isSuperAdmin && isLocked(dashboard.id) && !isUserLocked(dashboard.id) && (
          <UnlockDashboardModal
            projectId={dashboard.projectId}
            dashboardId={dashboard.id}
            opened={modal === "unlock"}
            close={closeModal}
          />
        )}
      </>
    </DashboardMenuContext.Provider>
  );
};
